import React, { useMemo, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchInput from './SearchInput';
import 'react-table/react-table.css';
import './DataTable.css';

// Coleta os accessors de todas as colunas, incluindo colunas agrupadas (columns aninhadas).
const flattenAccessors = (cols) =>
  cols.reduce((acc, col) => {
    if (col.columns) {
      return acc.concat(flattenAccessors(col.columns));
    }
    if (col.accessor) {
      acc.push(col.accessor);
    }
    return acc;
  }, []);

// Wrapper único sobre react-table: padroniza busca geral, paginação, loading, empty state e o
// card branco (com contagem total) para substituir as ~40 tabelas reimplementadas por view.
//
// Por padrão a tela usa só a busca geral (um campo que procura em todas as colunas). Telas que
// precisam do filtro coluna a coluna — as caixinhas embaixo de cada cabeçalho — passam
// `columnFilters`. É o caso de lançamentos e de alguns relatórios, onde se filtra por projeto,
// conta e data ao mesmo tempo, coisa que a busca geral não faz.
//
// A propriedade antiga `filterable` continua sendo engolida de propósito: cerca de 30 telas ainda
// a passam, herdado de antes da busca geral existir. Honrá-la ligaria as caixinhas em todas elas
// de uma vez. Por isso a reativação é explícita, tela a tela, via `columnFilters`.
// O ref é encaminhado para o ReactTable interno de propósito: várias telas exportam para Excel
// com this.selectTable.getResolvedState().sortedData, ou seja, exatamente o que está filtrado e
// ordenado na tela. Sem encaminhar, o ref não chega em lugar nenhum (componente de função) e a
// exportação quebra ao ser clicada.
const DataTable = forwardRef(({
  data,
  columns,
  loading,
  defaultPageSize,
  noDataText,
  showTotal,
  showSearch,
  searchPlaceholder,
  actions,
  filters,
  columnFilters,
  // Legado: ver o comentário no topo. Recebido aqui só para não vazar pro ReactTable.
  filterable, // eslint-disable-line no-unused-vars
  // Lógica de comparação do filtro por coluna. O nome que o react-table espera é
  // `defaultFilterMethod`; aceito `filterMethod` também porque é assim que as telas já
  // escrevem, e sem esse mapeamento a lógica delas (ignorar acento, comparar data
  // formatada) ficaria sem efeito.
  filterMethod,
  defaultFilterMethod,
  ...rest
}, ref) => {
  const [search, setSearch] = useState('');
  const accessors = useMemo(() => flattenAccessors(columns), [columns]);

  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const term = search.trim().toLowerCase();
    return data.filter((row) =>
      accessors.some((accessor) => {
        const value = row[accessor];
        return value != null && String(value).toLowerCase().includes(term);
      })
    );
  }, [data, accessors, search]);

  return (
    <div>
      {(showSearch || actions || filters) && (
        <div className="ds-search-bar">
          <div className="ds-search-left">
            {showSearch && (
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={searchPlaceholder}
              />
            )}
            {filters}
          </div>
          {actions && <div className="ds-search-actions">{actions}</div>}
        </div>
      )}
      <div className="ds-table-card">
        {showTotal && (
          <div className="ds-table-total">Total {filteredData.length} itens</div>
        )}
        <ReactTable
          ref={ref}
          data={filteredData}
          columns={columns}
          filterable={columnFilters}
          defaultFilterMethod={defaultFilterMethod || filterMethod}
          defaultPageSize={defaultPageSize}
          className="ds-table -highlight"
          loading={loading}
          LoadingComponent={({ loading: isLoading }) =>
            isLoading ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.7)',
                }}
              >
                <CircularProgress />
              </div>
            ) : null
          }
          noDataText={noDataText}
          previousText="Anterior"
          nextText="Próximo"
          pageText="Página"
          ofText="de"
          rowsText="linhas"
          {...rest}
        />
      </div>
    </div>
  );
});

DataTable.displayName = 'DataTable';

DataTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  noDataText: PropTypes.string,
  showTotal: PropTypes.bool,
  showSearch: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  actions: PropTypes.node,
  filters: PropTypes.node,
  columnFilters: PropTypes.bool,
  filterMethod: PropTypes.func,
  defaultFilterMethod: PropTypes.func,
};

DataTable.defaultProps = {
  loading: false,
  defaultPageSize: 20,
  noDataText: 'Nenhum registro encontrado',
  showTotal: true,
  showSearch: true,
  searchPlaceholder: 'Buscar...',
  columnFilters: false,
};

export default DataTable;
